import React, { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation } from '@apollo/react-hooks'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { cn } from '@/lib/utils'
import { snagStatusVariant, snagPriorityVariant } from '@/lib/status-variants'
import {
  Pencil,
  FileText,
  History,
  MapPin,
  AlertTriangle,
  Crosshair,
  Trash2,
  ChevronLeft,
  ChevronRight,
  LoaderCircle,
} from 'lucide-react'
import DatePicker from 'react-datepicker'
import * as lngs from 'date-fns/locale'
import i18n from '../../../i18n'
import 'react-datepicker/dist/react-datepicker.css'
import * as Query from '../../../api/gql/query'
import * as Mutation from '../../../api/gql/mutation'
import { useModuleActions } from '../../../hooks/useMyPermissions'
import { CREATE_SNAG_FROM_PHOTOS } from '../../photos/api'
import { describePhotosError, photosErrorKey } from '../../photos/lib/errors'
import { toastr } from 'react-redux-toastr'
import SnagFileUpload from './SnagFileUpload'
import SnagPinPreview from './blueprint/SnagPinPreview'
import SnagActivityLog from './SnagActivityLog'
import { useSnagActivityLogger } from '../../../utils/snagActivityLogger'
import { useSnagFieldChangeDetector } from '../../../utils/snagFieldChangeDetector'

interface SnagModalProps {
  isOpen: boolean
  onClose: (saved?: boolean) => void
  snagId?: string | null
  floorId?: string | null
  initialPosition?: { x: number; y: number } | null
  onChangePinLocation?: (snagId: string, floorId: string) => void
  canChangePinLocation?: boolean
  filteredSnagIds?: string[]
  onNavigate?: (snagId: string) => void
  pendingPinCoordinates?: { x: number; y: number } | null
  onShowOnBlueprint?: (snagId: string, floorId: string) => void
  /** Create mode seeded from gallery photos: submit goes through createSnagFromPhotos. */
  photoIds?: string[]
  photoThumbs?: string[]
  initialTrade?: string | null
}

interface FormData {
  title: string
  description: string
  status: string
  priority: string
  dueDate: Date | null
  infrastructureType: string
  assignedToId: string
  pinX: number | null
  pinY: number | null
  selectedFloorId: string
}

// Approval-tier statuses — mirrors APPROVAL_TIER_STATUSES in src/permissions/registry.ts.
// Setting a snag to one of these via updateSnag requires ISSUES.APPROVE on the backend
// (the same tier verifySnag/closeSnag/reopenSnag gate); EDIT-only users must not be
// offered them as a selectable target.
const APPROVAL_TIER_STATUSES = ['VERIFIED', 'CLOSED', 'REOPENED']

const SnagModal: React.FC<SnagModalProps> = ({
  isOpen,
  onClose,
  snagId,
  floorId,
  initialPosition,
  onChangePinLocation,
  canChangePinLocation = false,
  filteredSnagIds = [],
  onNavigate,
  pendingPinCoordinates,
  onShowOnBlueprint,
  photoIds,
  photoThumbs = [],
  initialTrade,
}) => {
  const { t } = useTranslation()
  const { data: meData } = useQuery(Query.ME)
  const currentProject = meData?.me?.currentProject
  const projectId = currentProject?.id

  const { canCreate, canEdit, canApprove } = useModuleActions('ISSUES', projectId)

  const { logSnagActivity } = useSnagActivityLogger()
  const { detectChanges, generateActivityLogs } = useSnagFieldChangeDetector()

  const [formData, setFormData] = useState<FormData>({
    title: '',
    description: '',
    status: 'OPEN',
    priority: 'MEDIUM',
    dueDate: null,
    infrastructureType: '',
    assignedToId: '',
    pinX: initialPosition?.x ?? null,
    pinY: initialPosition?.y ?? null,
    selectedFloorId: floorId || '',
  })
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [activeTab, setActiveTab] = useState('details')

  const { data: snagData, loading: snagLoading } = useQuery(Query.SNAG, {
    variables: { id: snagId },
    skip: !snagId,
  })

  const { data: membersData } = useQuery(Query.PROJECT_MEMBERS, {
    variables: { projectId },
    skip: !projectId,
  })

  const { data: buildingsData } = useQuery(Query.BUILDINGS, {
    variables: { projectId },
    skip: !projectId,
  })

  const { data: settingsData } = useQuery(Query.SNAG_SETTINGS, {
    variables: { projectId },
    skip: !projectId,
  })

  const allFloors: { id: string; name: string; buildingName: string }[] = []
  buildingsData?.buildings?.forEach((building: any) => {
    building.floors?.forEach((floor: any) => {
      allFloors.push({
        id: floor.id,
        name: floor.name,
        buildingName: building.name,
      })
    })
  })

  const [createSnag] = useMutation(Mutation.CREATE_SNAG)
  const [updateSnag] = useMutation(Mutation.UPDATE_SNAG)
  const [createSnagFromPhotos] = useMutation(CREATE_SNAG_FROM_PHOTOS, { context: { handlesErrors: true } })
  const isPhotoMode = !!photoIds?.length

  const snag = snagData?.snag

  useEffect(() => {
    if (isOpen) {
      if (snag) {
        setFormData({
          title: snag.title || '',
          description: snag.description || '',
          status: snag.status || 'OPEN',
          priority: snag.priority || 'MEDIUM',
          dueDate: snag.dueDate ? new Date(snag.dueDate) : null,
          infrastructureType: snag.infrastructureType || '',
          assignedToId: snag.assignedTo?.id || '',
          pinX: snag.pinX,
          pinY: snag.pinY,
          selectedFloorId: snag.floor?.id || '',
        })
      } else {
        setFormData({
          title: '',
          description: '',
          status: 'OPEN',
          priority: settingsData?.snagSettings?.defaultPriority || 'MEDIUM',
          dueDate: null,
          infrastructureType: initialTrade || '',
          assignedToId: '',
          pinX: initialPosition?.x ?? null,
          pinY: initialPosition?.y ?? null,
          selectedFloorId: floorId || '',
        })
      }
      setErrors({})
    }
  }, [isOpen, snag, initialPosition, settingsData])

  useEffect(() => {
    if (pendingPinCoordinates && isOpen) {
      setFormData((prev) => ({
        ...prev,
        pinX: pendingPinCoordinates.x,
        pinY: pendingPinCoordinates.y,
      }))
    }
  }, [pendingPinCoordinates, isOpen])

  const modifiedFields = useMemo(() => {
    if (!snag) return new Set<string>()
    const modified = new Set<string>()
    if (formData.title !== (snag.title || '')) modified.add('title')
    if (formData.description !== (snag.description || '')) modified.add('description')
    if (formData.status !== (snag.status || 'OPEN')) modified.add('status')
    if (formData.priority !== (snag.priority || 'MEDIUM')) modified.add('priority')
    if (formData.infrastructureType !== (snag.infrastructureType || '')) modified.add('infrastructureType')
    if (formData.assignedToId !== (snag.assignedTo?.id || '')) modified.add('assignedToId')
    const originalDueDate = snag.dueDate ? new Date(snag.dueDate).toISOString().split('T')[0] : ''
    const formDueDate = formData.dueDate ? formData.dueDate.toISOString().split('T')[0] : ''
    if (formDueDate !== originalDueDate) modified.add('dueDate')
    if (formData.pinX !== snag.pinX || formData.pinY !== snag.pinY) modified.add('pinLocation')
    return modified
  }, [snag, formData])

  const hasUnsavedChanges = modifiedFields.size > 0

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }))
    }
  }

  const handleSelectChange = (name: string, value: string) => {
    setFormData((prev) => ({ ...prev, [name]: value }))
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }))
    }
  }

  const validateForm = () => {
    const newErrors: Record<string, string> = {}
    if (!formData.title.trim()) {
      newErrors.title = t('Snags_validation_title_required')
    }
    if (!snagId && !formData.selectedFloorId) {
      newErrors.selectedFloorId = t('Snags_validation_floor_required')
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // The current assignee is kept in the list even if they have since left the
  // project, so an existing assignment stays visible and the activity log can
  // still name whoever it is replacing.
  const assignableUsers: { id: string; name: string }[] = (() => {
    const members = (membersData?.project?.users ?? [])
      .map((m: any) => m.user)
      .filter(Boolean)
    const assignee = snag?.assignedTo
    if (assignee && !members.some((u: any) => u.id === assignee.id)) members.push(assignee)
    return members
  })()

  const userMap: Record<string, string> = {}
  assignableUsers.forEach((user) => {
    userMap[user.id] = user.name
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validateForm()) return
    setIsSubmitting(true)
    setErrors({})

    try {
      if (snagId && snag) {
        const newData = {
          title: formData.title,
          description: formData.description || null,
          status: formData.status,
          priority: formData.priority,
          dueDate: formData.dueDate ? formData.dueDate.toISOString() : null,
          infrastructureType: formData.infrastructureType || null,
          assignedToId: formData.assignedToId || null,
          pinX: formData.pinX,
          pinY: formData.pinY,
        }

        const changes = detectChanges(snag, newData)

        await updateSnag({
          variables: { id: snagId, input: newData },
        })

        if (changes.length > 0) {
          const activityLogs = generateActivityLogs(changes, { userMap, oldData: snag, newData })
          for (const log of activityLogs) {
            await logSnagActivity({ snagId, title: log.title, description: log.description })
          }
        }

        setIsSubmitting(false)
        onClose(true)
      } else if (formData.selectedFloorId) {
        const input = {
          floorId: formData.selectedFloorId,
          title: formData.title,
          description: formData.description || null,
          priority: formData.priority,
          dueDate: formData.dueDate ? formData.dueDate.toISOString() : null,
          infrastructureType: formData.infrastructureType || null,
          assignedToId: formData.assignedToId || null,
          pinX: formData.pinX,
          pinY: formData.pinY,
        }
        const result: any = isPhotoMode
          ? await createSnagFromPhotos({ variables: { photoIds, input } })
          : await createSnag({ variables: { input } })

        const newSnagId = result?.data?.createSnag?.id ?? result?.data?.createSnagFromPhotos?.id
        if (newSnagId) {
          await logSnagActivity({
            snagId: newSnagId,
            title: t('Snags_activity_created', 'Snag Created'),
            description: formData.title,
          })
        }

        setIsSubmitting(false)
        if (isPhotoMode) toastr.success(t('Common_success'), t('Photos_defect_created'))
        onClose(true)
      }
    } catch (error: any) {
      console.error('Error saving snag:', error)
      setIsSubmitting(false)
      setErrors({
        submit: isPhotoMode
          ? describePhotosError(photosErrorKey(error), t)
          : error.message || t('Common_error_occurred', 'An error occurred'),
      })
    }
  }

  const handleClose = () => {
    if (!isSubmitting) onClose()
  }

  const handleRemovePin = () => {
    setFormData((prev) => ({ ...prev, pinX: null, pinY: null }))
  }

  const handleChangePinLocation = () => {
    if (onChangePinLocation && snagId && snag?.floor?.id) {
      onChangePinLocation(snagId, snag.floor.id)
    }
  }

  // Navigation
  const currentIndex = snagId && filteredSnagIds.length > 0 ? filteredSnagIds.indexOf(snagId) : -1
  const hasPrev = currentIndex > 0
  const hasNext = currentIndex >= 0 && currentIndex < filteredSnagIds.length - 1
  const canNavigate = filteredSnagIds.length > 1 && onNavigate && snagId

  const handleNavigatePrev = () => {
    if (hasPrev && onNavigate) onNavigate(filteredSnagIds[currentIndex - 1])
  }
  const handleNavigateNext = () => {
    if (hasNext && onNavigate) onNavigate(filteredSnagIds[currentIndex + 1])
  }

  const statusOptions = [
    { value: 'OPEN', label: t('Snags_status_open') },
    { value: 'IN_PROGRESS', label: t('Snags_status_in_progress') },
    { value: 'RESOLVED', label: t('Snags_status_resolved') },
    { value: 'VERIFIED', label: t('Snags_status_verified') },
    { value: 'CLOSED', label: t('Snags_status_closed') },
    { value: 'REOPENED', label: t('Snags_status_reopened') },
  ]

  // EDIT-only users can't select INTO an approval-tier status (matches the backend
  // updateSnag policy — see registry.ts), but a snag already sitting in one must keep
  // displaying it, so the current value is always kept even when not otherwise selectable.
  const visibleStatusOptions = statusOptions.filter(
    (o) => canApprove || !APPROVAL_TIER_STATUSES.includes(o.value) || o.value === formData.status,
  )

  const priorityOptions = [
    { value: 'LOW', label: t('Snags_priority_low') },
    { value: 'MEDIUM', label: t('Snags_priority_medium') },
    { value: 'HIGH', label: t('Snags_priority_high') },
    { value: 'CRITICAL', label: t('Snags_priority_critical') },
  ]

  const infrastructureTypes = settingsData?.snagSettings?.infrastructureTypes || []
  const isEditMode = !!snagId

  const ModifiedBadge = () => (
    <Badge variant="warning" className="ml-2 text-[10px] px-1 py-0">
      {t('Common_modified', 'Modified')}
    </Badge>
  )

  const detailsContent = (
    <div className="space-y-4 rounded-md border bg-card p-4">
      {isPhotoMode && photoThumbs.length > 0 && (
        <div className="flex gap-2 overflow-x-auto">
          {photoThumbs.map((url, i) => (
            <img key={i} src={url} alt="" className="h-14 w-14 shrink-0 rounded-md border object-cover" />
          ))}
        </div>
      )}
      {/* Status & Priority Bar (edit mode) */}
      {isEditMode && snag && (
        <div className="flex items-center gap-4 rounded-md bg-muted/50 p-3">
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">{t('Snags_field_status')}:</span>
            <Badge variant={snagStatusVariant(formData.status)} className="font-medium">
              {t(`Snags_status_${formData.status.toLowerCase()}`)}
            </Badge>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">{t('Snags_field_priority')}:</span>
            <Badge variant={snagPriorityVariant(formData.priority)} className="font-medium">
              {t(`Snags_priority_${formData.priority.toLowerCase()}`)}
            </Badge>
          </div>
        </div>
      )}

      {/* Floor Selector (create mode) */}
      {!isEditMode && (
        <div className="space-y-2">
          <Label className="text-sm font-medium">
            {t('Snags_field_floor')} <span className="text-destructive">*</span>
          </Label>
          <Select value={formData.selectedFloorId} onValueChange={(v) => handleSelectChange('selectedFloorId', v)}>
            <SelectTrigger className={cn(errors.selectedFloorId && 'border-destructive')}>
              <SelectValue placeholder={t('Snags_field_select_floor')} />
            </SelectTrigger>
            <SelectContent>
              {allFloors.map((floor) => (
                <SelectItem key={floor.id} value={floor.id}>
                  {floor.buildingName} - {floor.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.selectedFloorId && <p className="text-xs text-destructive">{errors.selectedFloorId}</p>}
        </div>
      )}

      {/* Title */}
      <div className={cn('space-y-2', isEditMode && modifiedFields.has('title') && 'border-l-2 border-amber-400 pl-3')}>
        <Label className="text-sm font-medium">
          {t('Snags_field_title')} <span className="text-destructive">*</span>
          {modifiedFields.has('title') && <ModifiedBadge />}
        </Label>
        <Input
          name="title"
          value={formData.title}
          onChange={handleInputChange}
          placeholder={t('Snags_field_title_placeholder')}
          className={cn(errors.title && 'border-destructive')}
        />
        {errors.title && <p className="text-xs text-destructive">{errors.title}</p>}
      </div>

      {/* Description */}
      <div className={cn('space-y-2', isEditMode && modifiedFields.has('description') && 'border-l-2 border-amber-400 pl-3')}>
        <Label className="text-sm font-medium">
          {t('Snags_field_description')}
          {modifiedFields.has('description') && <ModifiedBadge />}
        </Label>
        <Textarea
          name="description"
          value={formData.description}
          onChange={handleInputChange}
          placeholder={t('Snags_field_description_placeholder')}
          rows={3}
        />
      </div>

      {/* Status & Priority */}
      <div className="grid grid-cols-2 gap-4">
        <div className={cn('space-y-2', isEditMode && modifiedFields.has('status') && 'border-l-2 border-amber-400 pl-3')}>
          <Label className="text-sm font-medium">
            {t('Snags_field_status')}
            {modifiedFields.has('status') && <ModifiedBadge />}
          </Label>
          <Select value={formData.status} onValueChange={(v) => handleSelectChange('status', v)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {visibleStatusOptions.map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className={cn('space-y-2', isEditMode && modifiedFields.has('priority') && 'border-l-2 border-amber-400 pl-3')}>
          <Label className="text-sm font-medium">
            {t('Snags_field_priority')}
            {modifiedFields.has('priority') && <ModifiedBadge />}
          </Label>
          <Select value={formData.priority} onValueChange={(v) => handleSelectChange('priority', v)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {priorityOptions.map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Infrastructure Type & Due Date */}
      <div className="grid grid-cols-2 gap-4">
        <div className={cn('space-y-2', isEditMode && modifiedFields.has('infrastructureType') && 'border-l-2 border-amber-400 pl-3')}>
          <Label className="text-sm font-medium">
            {t('Snags_field_infrastructure_type')}
            {modifiedFields.has('infrastructureType') && <ModifiedBadge />}
          </Label>
          <Select value={formData.infrastructureType || '_none_'} onValueChange={(v) => handleSelectChange('infrastructureType', v === '_none_' ? '' : v)}>
            <SelectTrigger>
              <SelectValue placeholder={t('Snags_field_select_infrastructure')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="_none_">{t('Snags_field_select_infrastructure')}</SelectItem>
              {infrastructureTypes.map((type: string) => (
                <SelectItem key={type} value={type}>{type}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className={cn('space-y-2', isEditMode && modifiedFields.has('dueDate') && 'border-l-2 border-amber-400 pl-3')}>
          <Label className="text-sm font-medium">
            {t('Snags_field_due_date')}
            {modifiedFields.has('dueDate') && <ModifiedBadge />}
          </Label>
          <DatePicker
            selected={formData.dueDate}
            onChange={(date: Date | null) => setFormData((prev) => ({ ...prev, dueDate: date }))}
            dateFormat="yyyy/MM/dd"
            className="flex h-9 w-full rounded-md border border-input bg-card px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            locale={(lngs as any)[i18n.language]}
            isClearable
            autoComplete="off"
            placeholderText={t('Snags_field_due_date')}
          />
        </div>
      </div>

      {/* Assigned To */}
      <div className={cn('space-y-2', isEditMode && modifiedFields.has('assignedToId') && 'border-l-2 border-amber-400 pl-3')}>
        <Label className="text-sm font-medium">
          {t('Snags_field_assigned_to')}
          {modifiedFields.has('assignedToId') && <ModifiedBadge />}
        </Label>
        <Select value={formData.assignedToId || '_none_'} onValueChange={(v) => handleSelectChange('assignedToId', v === '_none_' ? '' : v)}>
          <SelectTrigger>
            <SelectValue placeholder={t('Snags_field_select_user')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="_none_">{t('Snags_field_select_user')}</SelectItem>
            {assignableUsers.map((user) => (
              <SelectItem key={user.id} value={user.id}>{user.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Pin Location */}
      <div className={cn(
        'rounded-md border p-3',
        modifiedFields.has('pinLocation') ? 'border-amber-400 bg-amber-50/50' : 'bg-muted/30',
      )}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MapPin className={cn('h-5 w-5 shrink-0', formData.pinX !== null ? 'text-green-600' : 'text-muted-foreground')} />
            <div>
              <p className="text-sm font-medium mb-0">
                {t('Snags_pin_location', 'Pin Location')}
                {isEditMode && modifiedFields.has('pinLocation') && <ModifiedBadge />}
              </p>
              {formData.pinX !== null && formData.pinY !== null ? (
                <p className="text-xs text-green-600 mb-0">
                  {t('Snags_pin_set', 'Location set on blueprint')} (X: {formData.pinX.toFixed(1)}%, Y: {formData.pinY.toFixed(1)}%)
                </p>
              ) : (
                <p className="text-xs text-muted-foreground mb-0">
                  {t('Snags_pin_not_set', 'No location set')}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isEditMode && canChangePinLocation && canEdit && (
              <Button
                type="button"
                variant={formData.pinX !== null ? 'outline' : 'default'}
                size="sm"
                onClick={handleChangePinLocation}
              >
                <Crosshair className="h-4 w-4" />
                {formData.pinX !== null ? t('Snags_change_location', 'Change') : t('Snags_add_location', 'Add')}
              </Button>
            )}
            {formData.pinX !== null && formData.pinY !== null && (!isEditMode || canEdit) && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="text-destructive hover:text-destructive"
                onClick={handleRemovePin}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
        {formData.pinX !== null &&
          formData.pinY !== null &&
          snag?.floor?.blueprintFile?.blueprintDzi && (
            <div className="mt-3">
              <SnagPinPreview
                blueprintDzi={snag.floor.blueprintFile.blueprintDzi}
                pinX={formData.pinX}
                pinY={formData.pinY}
                status={formData.status}
                onClick={
                  onShowOnBlueprint && snagId && snag?.floor?.id
                    ? () => onShowOnBlueprint(snagId, snag.floor.id)
                    : undefined
                }
              />
            </div>
          )}
      </div>
    </div>
  )

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) handleClose() }}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="h-5 w-5 text-primary" />
              {isEditMode ? t('Snags_modal_edit_title') : isPhotoMode ? t('Photos_defect_title', { count: photoIds?.length }) : t('Snags_modal_create_title')}
              {isEditMode && snag?.displayNumber && (
                <Badge variant="outline" className="font-bold">{snag.displayNumber}</Badge>
              )}
            </DialogTitle>

            {canNavigate && (
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={handleNavigatePrev} disabled={!hasPrev}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm text-muted-foreground min-w-[50px] text-center">
                  {currentIndex + 1} / {filteredSnagIds.length}
                </span>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={handleNavigateNext} disabled={!hasNext}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
        </DialogHeader>

        {/* Body — p-1.5/-m-1.5: overflow-y-auto also clips horizontally, so the
            scroll box needs interior room for the 4px focus ring; the negative
            margin keeps the fields aligned with the dialog padding. */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto min-h-0 p-1.5 -m-1.5">
          {snagLoading && (
            <div className="flex flex-col items-center justify-center py-8">
              <LoaderCircle className="h-8 w-8 animate-spin text-primary" />
              <p className="mt-2 text-sm text-muted-foreground">{t('Common_loading')}</p>
            </div>
          )}

          {errors.submit && (
            <div className="flex items-center gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700 mb-4">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              {errors.submit}
            </div>
          )}

          {!snagLoading && (
            <>
              {isEditMode && snag ? (
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                  <TabsList className="mb-3">
                    <TabsTrigger value="details" className="gap-1.5">
                      <Pencil className="h-3.5 w-3.5" />
                      {t('Snags_tab_details', 'Details')}
                    </TabsTrigger>
                    <TabsTrigger value="files" className="gap-1.5">
                      <FileText className="h-3.5 w-3.5" />
                      {t('Snags_tab_files', 'Files')}
                      {snag.files?.length > 0 && (
                        <Badge variant="secondary" className="text-[10px] px-1.5 py-0 ml-1">{snag.files.length}</Badge>
                      )}
                    </TabsTrigger>
                    <TabsTrigger value="history" className="gap-1.5">
                      <History className="h-3.5 w-3.5" />
                      {t('Snags_tab_history', 'History')}
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="details">{detailsContent}</TabsContent>
                  <TabsContent value="files">
                    <SnagFileUpload snagId={snag.id} existingFiles={snag.files || []} disabled={isSubmitting || !canEdit} />
                  </TabsContent>
                  <TabsContent value="history">
                    <SnagActivityLog snagId={snag.id} />
                  </TabsContent>
                </Tabs>
              ) : (
                detailsContent
              )}
            </>
          )}

          {/* Footer */}
          <div className="flex items-center justify-end gap-2 pt-4 border-t mt-4">
            <Button type="button" variant="outline" onClick={handleClose} disabled={isSubmitting}>
              {t('Common_cancel')}
            </Button>
            {(isEditMode ? canEdit : canCreate) && (
              <Button
                type="submit"
                disabled={isSubmitting || snagLoading || (isEditMode && !hasUnsavedChanges)}
                title={isEditMode && !hasUnsavedChanges ? t('Snags_no_changes', 'No changes to save') : ''}
              >
                {isSubmitting && <LoaderCircle className="h-4 w-4 animate-spin" />}
                {isSubmitting ? t('Common_saving') : isEditMode ? t('Common_update') : t('Common_create')}
              </Button>
            )}
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export default SnagModal
